import React, { useState, useEffect, useContext } from 'react';
import "./table.scss";
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { collection, getDocs, query, orderBy, where, Timestamp, limit } from "firebase/firestore";
import { db } from '../../firebase';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { AuthContext } from "../../context/AuthContext";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

// Mantener la función para calcular correctamente la duración de jornadas
const calcularDuracionJornada = (inicio, fin) => {
  if (!inicio || !fin) return null;
  
  // Aseguramos que estamos trabajando con objetos Date
  const inicioDate = inicio instanceof Date ? inicio : new Date(inicio);
  const finDate = fin instanceof Date ? fin : new Date(fin);
  
  // Calculamos la diferencia en milisegundos
  const diferenciaMilisegundos = finDate.getTime() - inicioDate.getTime();
  
  // Convertimos a horas y redondeamos a 2 decimales
  const horas = diferenciaMilisegundos / (1000 * 60 * 60);
  
  // Si la duración es negativa, asumimos que hay un cruce de fecha (fin al día siguiente)
  if (horas < 0) {
    console.warn('Detectada duración negativa, posible registro con fechas incorrectas:', 
      { 
        inicio: inicioDate.toISOString(), 
        fin: finDate.toISOString(),
        horas
      }
    );
    // Tomamos el valor absoluto como fallback, pero debería revisarse este registro
    return Math.abs(horas);
  }
  
  return Math.round(horas * 100) / 100;
};

/**
 * Función auxiliar que detecta si una jornada cruza de un día a otro
 */
const detectaCruceDia = (inicioTimestamp, finTimestamp) => {
  if (!inicioTimestamp || !finTimestamp) return false;
  
  const inicio = new Date(inicioTimestamp);
  const fin = new Date(finTimestamp);
  
  // Si el día del mes es diferente, cruza día
  if (inicio.getDate() !== fin.getDate() || 
      inicio.getMonth() !== fin.getMonth() || 
      inicio.getFullYear() !== fin.getFullYear()) {
    return true;
  }
  
  // Si el día es el mismo pero la hora de fin es menor que la de inicio,
  // probablemente cruza día (aunque registrado con fecha incorrecta)
  if (fin.getHours() < inicio.getHours()) {
    return true;
  }
  
  return false;
};

// Componente para mostrar el mapa con la ubicación
const LocationDialog = ({ open, handleClose, location }) => {
  if (!location || !location.coordinates) return null;
  
  const { latitude, longitude } = location.coordinates;
  const mapUrl = `https://maps.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`;
  
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="location-dialog-title"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle id="location-dialog-title">
        Ubicación del registro
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <iframe
              src={mapUrl}
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6">Detalles de la ubicación</Typography>
          </Grid>
          {location.address && (
            <>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1">Dirección</Typography>
                <Typography variant="body1">
                  {location.address.street}, {location.address.postalCode}
                </Typography>
                <Typography variant="body1">
                  {location.address.city}, {location.address.region}
                </Typography>
                <Typography variant="body1">
                  {location.address.country}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1">Coordenadas</Typography>
                <Typography variant="body1">
                  Latitud: {latitude}
                </Typography>
                <Typography variant="body1">
                  Longitud: {longitude}
                </Typography>
                <Typography variant="body1">
                  Precisión: {location.coordinates.accuracy} m
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cerrar
        </Button>
        <Button 
          color="primary" 
          onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`, '_blank')}
        >
          Abrir en Google Maps
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const JornadasTable = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  const { currentService } = useContext(AuthContext);
  
  // Estados para el filtro de fechas
  const [fechaInicio, setFechaInicio] = useState(new Date(new Date().setDate(new Date().getDate() - 30))); // Por defecto, 30 días atrás
  const [fechaFin, setFechaFin] = useState(new Date());
  
  // Formatear fechas para los inputs
  const formatDateForInput = (date) => {
    return date.toISOString().split('T')[0];
  };
  
  // Estado para el diálogo de ubicación
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const handleOpenLocationDialog = (location) => {
    setSelectedLocation(location);
    setLocationDialogOpen(true);
  };

  const handleCloseLocationDialog = () => {
    setLocationDialogOpen(false);
  };
  
  const fetchJornadas = async () => {
    try {
      setLoading(true);
      if (!currentService?.id) {
        console.error("ID de servicio no disponible");
        return;
      }

      console.log("Cargando registros de jornada para servicio:", currentService.id);
      console.log("Rango de fechas:", fechaInicio, fechaFin);
      
      // Crear una copia de fechaFin para evitar mutación
      const fechaFinCopy = new Date(fechaFin);
      
      // Convertir fechas a Timestamp para Firebase
      const startTimestamp = Timestamp.fromDate(fechaInicio);
      const endTimestamp = Timestamp.fromDate(new Date(fechaFinCopy.setHours(23, 59, 59, 999))); // Final del día
      
      // Obtener registros con filtrado por fecha
      const registrosRef = collection(db, `servicios/${currentService.id}/registrosJornada`);
      const q = query(
        registrosRef, 
        where('timestamp', '>=', startTimestamp),
        where('timestamp', '<=', endTimestamp),
        orderBy('timestamp', 'desc'),
        limit(1000)
      );
      
      const querySnapshot = await getDocs(q);
      
      console.log(`Registros encontrados: ${querySnapshot.size}`);
      
      // Convertir los documentos a objetos más manejables
      const registros = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const timestamp = data.timestamp?.toDate();
        
        return {
          id: doc.id,
          ...data,
          timestamp: timestamp,
          tipo: data.tipo,
          vigilanteId: data.vigilanteId,
          vigilanteNombre: data.vigilanteNombre,
          vigilanteApellidos: data.vigilanteApellidos,
          vigilanteTipVS: data.vigilanteTipVS,
          vigilanteTiendas: data.vigilanteTiendas,
          vigilanteEmail: data.vigilanteEmail,
          vigilanteTelefono: data.vigilanteTelefono,
          vigilanteBorrado: data.vigilanteBorrado,
          ubicacion: data.ubicacion // Añadimos la ubicación
        };
      });
      
      // Separar por tipo y agrupar por vigilante
      const registrosPorVigilante = {};
      
      registros.forEach(registro => {
        if (!registro.vigilanteId) return;
        
        if (!registrosPorVigilante[registro.vigilanteId]) {
          registrosPorVigilante[registro.vigilanteId] = {
            inicios: [],
            fines: []
          };
        }
        
        if (registro.tipo === 'inicio') {
          registrosPorVigilante[registro.vigilanteId].inicios.push(registro);
        } else if (registro.tipo === 'fin') {
          registrosPorVigilante[registro.vigilanteId].fines.push(registro);
        }
      });
      
      // Array para las jornadas pareadas
      const jornadasPareadas = [];
      
      // Procesar cada vigilante
      Object.entries(registrosPorVigilante).forEach(([vigilanteId, datos]) => {
        // Ordenar por timestamp (más reciente primero)
        datos.inicios.sort((a, b) => b.timestamp - a.timestamp);
        datos.fines.sort((a, b) => b.timestamp - a.timestamp);
        
        // Procesar los fines primero
        datos.fines.forEach(fin => {
          // Buscar el inicio correspondiente (el más reciente anterior a este fin)
          const inicioCorrespondiente = datos.inicios.find(inicio => 
            inicio.timestamp && fin.timestamp && 
            inicio.timestamp < fin.timestamp &&
            // Verificar que este inicio no ha sido usado ya
            !jornadasPareadas.some(j => j.idInicio === inicio.id)
          );
          
          if (inicioCorrespondiente) {
            // Calcular duración
            const duracion = calcularDuracionJornada(inicioCorrespondiente.timestamp, fin.timestamp);
            const cruzaDia = detectaCruceDia(inicioCorrespondiente.timestamp, fin.timestamp);
            
            // Crear jornada pareada
            jornadasPareadas.push({
              id: `${inicioCorrespondiente.id}-${fin.id}`,
              idInicio: inicioCorrespondiente.id,
              idFin: fin.id,
              fechaInicio: inicioCorrespondiente.timestamp,
              fechaFin: fin.timestamp,
              duracion: duracion,
              fechaCruzada: cruzaDia,
              vigilanteId: vigilanteId,
              vigilanteNombre: inicioCorrespondiente.vigilanteNombre || fin.vigilanteNombre,
              vigilanteApellidos: inicioCorrespondiente.vigilanteApellidos || fin.vigilanteApellidos,
              email: inicioCorrespondiente.vigilanteEmail || fin.vigilanteEmail,
              tip: inicioCorrespondiente.vigilanteTipVS || fin.vigilanteTipVS,
              telefono: inicioCorrespondiente.vigilanteTelefono || fin.vigilanteTelefono,
              tiendas: inicioCorrespondiente.vigilanteTiendas || fin.vigilanteTiendas,
              borrado: inicioCorrespondiente.vigilanteBorrado || fin.vigilanteBorrado,
              ubicacionInicio: inicioCorrespondiente.ubicacion,
              ubicacionFin: fin.ubicacion
            });
          } else {
            // Fin sin inicio correspondiente (poco común, pero posible)
            console.warn(`Fin sin inicio correspondiente para vigilante ${vigilanteId}: ${fin.id}`);
            jornadasPareadas.push({
              id: `fin-${fin.id}`,
              idFin: fin.id,
              fechaFin: fin.timestamp,
              duracion: 'Jornada incompleta',
              vigilanteId: vigilanteId,
              vigilanteNombre: fin.vigilanteNombre,
              vigilanteApellidos: fin.vigilanteApellidos,
              email: fin.vigilanteEmail,
              tip: fin.vigilanteTipVS,
              telefono: fin.vigilanteTelefono,
              tiendas: fin.vigilanteTiendas,
              borrado: fin.vigilanteBorrado,
              ubicacionFin: fin.ubicacion
            });
          }
        });
        
        // Procesar inicios sin fin (jornadas activas)
        datos.inicios.forEach(inicio => {
          // Verificar si este inicio ya fue emparejado
          const yaEmparejado = jornadasPareadas.some(j => j.idInicio === inicio.id);
          
          if (!yaEmparejado) {
            jornadasPareadas.push({
              id: `inicio-${inicio.id}`,
              idInicio: inicio.id,
              fechaInicio: inicio.timestamp,
              duracion: 'Jornada no finalizada',
              vigilanteId: vigilanteId,
              vigilanteNombre: inicio.vigilanteNombre,
              vigilanteApellidos: inicio.vigilanteApellidos,
              email: inicio.vigilanteEmail,
              tip: inicio.vigilanteTipVS,
              telefono: inicio.vigilanteTelefono,
              tiendas: inicio.vigilanteTiendas,
              borrado: inicio.vigilanteBorrado,
              ubicacionInicio: inicio.ubicacion
            });
          }
        });
      });
      
      // Convertir tiendas a formato de texto
      const jornadasFormateadas = jornadasPareadas.map(jornada => ({
        ...jornada,
        tiendas: Array.isArray(jornada.tiendas) 
          ? jornada.tiendas.map(t => t.numero).join(', ') 
          : 'N/A'
      }));
      
      console.log(`Jornadas pareadas procesadas: ${jornadasFormateadas.length}`);
      setRows(jornadasFormateadas);
    } catch (error) {
      console.error('Error al cargar las jornadas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentService?.id) {
      fetchJornadas();
    }
  }, [currentService]);

  const columns = [
    {
      field: 'fechaInicio',
      headerName: 'Inicio Jornada',
      width: 180,
      valueFormatter: (params) => 
        params.value ? format(params.value, "dd/MM/yyyy HH:mm", { locale: es }) : 'No registrado',
    },
    {
      field: 'ubicacionInicio',
      headerName: 'Ubicación Inicio',
      width: 130,
      renderCell: (params) => {
        if (!params.value) return 'No registrado';
        return (
          <Tooltip title="Ver ubicación de inicio">
            <IconButton 
              color="primary" 
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenLocationDialog(params.value);
              }}
            >
              <LocationOnIcon />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: 'fechaFin',
      headerName: 'Fin Jornada',
      width: 180,
      valueFormatter: (params) => {
        if (!params.value) return 'No registrado';
        
        // Verificar si existe la propiedad fechaCruzada de forma segura
        const isCruzada = params.row && params.row.fechaCruzada === true;
        
        const fechaFormateada = format(params.value, "dd/MM/yyyy HH:mm", { locale: es });
        
        // Solo añadimos indicador si la fecha está marcada como cruzada
        if (isCruzada) {
          return `${fechaFormateada} (día sig.)`;
        }
        return fechaFormateada;
      },
      cellClassName: (params) => {
        // Verificamos de forma segura
        return params.row && params.row.fechaCruzada === true ? 'fecha-cruzada' : '';
      },
    },
    {
      field: 'ubicacionFin',
      headerName: 'Ubicación Fin',
      width: 130,
      renderCell: (params) => {
        if (!params.value) return 'No registrado';
        return (
          <Tooltip title="Ver ubicación de fin">
            <IconButton 
              color="primary" 
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenLocationDialog(params.value);
              }}
            >
              <LocationOnIcon />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: 'duracion',
      headerName: 'Duración (horas)',
      width: 150,
      valueFormatter: (params) => 
        typeof params.value === 'number' ? `${params.value} h` : params.value,
    },
    {
      field: 'vigilanteNombre',
      headerName: 'Nombre',
      width: 200,
      valueGetter: (params) => 
        `${params.row.vigilanteNombre || ''} ${params.row.vigilanteApellidos || ''}`.trim(),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 220,
    },
    {
      field: 'tiendas',
      headerName: 'Servicios',
      width: 180,
    }
  ];

  const handleShowDeletedChange = (event) => {
    setShowDeleted(event.target.checked);
  };

  const filteredRows = rows.filter(row => showDeleted ? row.borrado : !row.borrado);

  const customLocaleText = {
    noRowsLabel: 'No hay registros',
    footerTotalRows: 'Total de registros:',
    columnMenuLabel: 'Menú',
    columnMenuShowColumns: 'Mostrar columnas',
    columnMenuFilter: 'Filtrar',
    columnMenuHideColumn: 'Ocultar',
    columnMenuUnsort: 'Desordenar',
    columnMenuSortAsc: 'Ordenar ASC',
    columnMenuSortDesc: 'Ordenar DESC',
  };

  return (
    <div className="datatable">
      <div className="datatableTitle">
        <h2 className="title-registro">Registro de Jornadas</h2>
        <div className="rightActions">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <TextField
              label="Fecha inicio"
              type="date"
              size="small"
              defaultValue={formatDateForInput(fechaInicio)}
              onChange={(e) => {
                setFechaInicio(new Date(e.target.value));
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              label="Fecha fin"
              type="date"
              size="small"
              defaultValue={formatDateForInput(fechaFin)}
              onChange={(e) => {
                setFechaFin(new Date(e.target.value));
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Button 
              variant="contained" 
              color="primary" 
              onClick={fetchJornadas}
            >
              Filtrar
            </Button>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={showDeleted}
                onChange={handleShowDeletedChange}
                color="primary"
              />
            }
            label="Mostrar borrados"
          />
        </div>
      </div>
      <DataGridPro
        className="datagrid"
        rows={filteredRows}
        columns={columns}
        loading={loading}
        pageSize={20}
        rowsPerPageOptions={[10, 20, 50, 100]}
        pagination
        checkboxSelection
        disableRowSelectionOnClick
        slots={{ toolbar: GridToolbar }}
        initialState={{
          sorting: {
            sortModel: [{ field: 'fechaInicio', sort: 'desc' }],
          },
          columns: {
            columnVisibilityModel: {
              id: false,
              idInicio: false,
              idFin: false,
              vigilanteId: false
            },
          },
        }}
        sx={{
          '& .sin-fin': {
            backgroundColor: '#fff3e0',
          },
          '& .fecha-cruzada': {
            color: '#ff6d00',
            fontWeight: 'bold'
          },
          '& .MuiDataGrid-footerContainer': {
            display: 'flex',
          }
        }}
        localeText={customLocaleText}
      />
      
      {/* Diálogo para mostrar la ubicación */}
      <LocationDialog 
        open={locationDialogOpen} 
        handleClose={handleCloseLocationDialog} 
        location={selectedLocation} 
      />
    </div>
  );
};

export default JornadasTable;